import type { GetStaticProps, GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';
import { OrganizationJsonLd } from 'next-seo';
import type { ParsedUrlQuery } from 'querystring';
import { useEffect } from 'react';

import LogoAnimate from '@/components/atoms/LogoAnimate';
import RelatedCompanies from '@/components/organisms/RelatedCompanies';
import FilterOverlay from '@/layouts/FilterOverlay';
import Footer from '@/layouts/Footer';
import LeftPanel from '@/layouts/LeftPanel';
import { Meta } from '@/layouts/Meta';
import RightPanel from '@/layouts/RightPanel';
import { getVentureBySlug } from '@/lib/api/Venture.api';
import { getVenturesHome } from '@/lib/api/Ventures.api';
import { Main } from '@/templates/Main';
import type CompanyProps from '@/types/Company.type';
import { logCustomEvent } from '@/utils/analytic';
import { AppConfig } from '@/utils/AppConfig';
import { AnalyticEvent } from '@/utils/enum';

interface VentureDetailParams extends ParsedUrlQuery {
  slug: string;
}

type IVentureDetailProps = {
  preview?: boolean | undefined;
  company: CompanyProps;
  relevantCompany: CompanyProps[];
};

export const VentureDetail = ({
  company,
  relevantCompany,
}: IVentureDetailProps) => {
  const router = useRouter();

  useEffect(() => {
    logCustomEvent(AnalyticEvent.ViewCompanyDetail);
  }, []);

  if (router.isFallback) {
    return <LogoAnimate />;
  }

  return (
    <Main
      screenSize="mx-auto max-w-screen-xl"
      isStickyNav
      meta={
        <Meta
          title={`${company.name} | ${AppConfig.site_name}`}
          description={company.short_description}
          canonical={`${process.env.NEXT_PUBLIC_WEB_URL}/${company.slug}`}
        />
      }
    >
      <OrganizationJsonLd
        logo={company.logo.url}
        name={company.name}
        email={company.contact_email}
        sameAs={[company.website ?? '']}
        url={company.website ?? ''}
        foundingDate={company.founding_year}
      />

      <div className="mt-5 mb-2 flex grow flex-col px-2 md:mt-10 lg:flex-row">
        <div className="grow p-3">
          <LeftPanel company={company} />
        </div>
        <div className="flex-none p-3 lg:w-1/3 xl:w-1/4">
          <RightPanel company={company} />
        </div>
      </div>
      <RelatedCompanies companies={relevantCompany} />
      <div className="mx-5 mt-6">
        <hr />
        <Footer />
      </div>
      <FilterOverlay />
    </Main>
  );
};

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  try {
    const params = context.params as VentureDetailParams;
    const isPreview = (await context.preview) || false;
    const { companyDetail: company, relevantCompany } = await getVentureBySlug(
      params?.slug
    );

    if (!company) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        isPreview,
        company,
        relevantCompany,
      },
      // read more: https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
      // revalidate: 60, // 30 second
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

export async function getStaticPaths() {
  const { data: companies } = await getVenturesHome();
  const paths = companies.map(({ slug }: CompanyProps) => {
    return {
      params: { slug },
    };
  });

  return {
    paths,
    // More about fallback
    // https://nextjs.org/docs/api-reference/data-fetching/get-static-paths#fallback-false
    fallback: true,
  };
}

export default VentureDetail;
